/**
 * ReactiveRequestResolver - Utility for resolving reactive requests
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for resolving, rejecting, and cleaning up
 * reactive requests in the worker client.
 */

/**
 * Options for resolving a reactive request
 */
export interface ResolveRequestOptions {
  clearTimeout?: boolean;
  deleteFromMap?: boolean;
  updateTelemetry?: boolean;
}

/**
 * Reactive request structure
 */
export interface ReactiveRequest<T = any> {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timeout: NodeJS.Timeout;
  startTime?: number;
  operation?: string;
}

/**
 * Resolve a reactive request
 * 
 * Gets a request from the map, clears its timeout, optionally updates telemetry,
 * and resolves it with the provided value.
 * 
 * @param reactiveRequests - Map of reactive requests
 * @param layoutId - Layout ID to resolve
 * @param value - Value to resolve with
 * @param options - Options for resolution behavior
 * @returns True if request was found and resolved
 */
export function resolveReactiveRequest<T>(
  reactiveRequests: Map<number | string, ReactiveRequest<T>>,
  layoutId: number | string,
  value: T,
  options: ResolveRequestOptions = {}
): boolean {
  const {
    clearTimeout: shouldClearTimeout = true,
    deleteFromMap: shouldDelete = true,
    updateTelemetry = false
  } = options;

  const request = reactiveRequests.get(layoutId);
  if (!request) {
    return false;
  }

  if (shouldClearTimeout) {
    clearTimeout(request.timeout);
  }

  if (shouldDelete) {
    reactiveRequests.delete(layoutId);
  }

  // Note: Telemetry update is handled separately as it requires access to telemetryData
  // This utility focuses on request resolution only

  request.resolve(value);
  return true;
}

/**
 * Reject a reactive request
 * 
 * Gets a request from the map, clears its timeout, and rejects it with an error.
 * 
 * @param reactiveRequests - Map of reactive requests
 * @param layoutId - Layout ID to reject
 * @param error - Error to reject with
 * @param options - Options for rejection behavior
 * @returns True if request was found and rejected
 */
export function rejectReactiveRequest(
  reactiveRequests: Map<number | string, ReactiveRequest>,
  layoutId: number | string,
  error: Error | string,
  options: ResolveRequestOptions = {}
): boolean {
  const {
    clearTimeout: shouldClearTimeout = true,
    deleteFromMap: shouldDelete = true
  } = options;

  const request = reactiveRequests.get(layoutId);
  if (!request) {
    return false;
  }

  if (shouldClearTimeout) {
    clearTimeout(request.timeout);
  }

  if (shouldDelete) {
    reactiveRequests.delete(layoutId);
  }

  const errorObj = error instanceof Error ? error : new Error(error);
  request.reject(errorObj);
  return true;
}

/**
 * Get a reactive request without resolving it
 * 
 * Useful for checking if a request exists or accessing its properties.
 * 
 * @param reactiveRequests - Map of reactive requests
 * @param layoutId - Layout ID to get
 * @returns Request if found, undefined otherwise
 */
export function getReactiveRequest<T>(
  reactiveRequests: Map<number | string, ReactiveRequest<T>>,
  layoutId: number | string
): ReactiveRequest<T> | undefined {
  return reactiveRequests.get(layoutId);
}

/**
 * Cleanup a reactive request (clear timeout and delete from map)
 * 
 * Removes a request from the map and clears its timeout without resolving or rejecting.
 * 
 * @param reactiveRequests - Map of reactive requests
 * @param layoutId - Layout ID to cleanup
 * @returns True if request was found and cleaned up
 */
export function cleanupReactiveRequest(
  reactiveRequests: Map<number | string, ReactiveRequest>,
  layoutId: number | string
): boolean {
  const request = reactiveRequests.get(layoutId);
  if (!request) {
    return false;
  }

  clearTimeout(request.timeout);
  reactiveRequests.delete(layoutId);
  return true;
}

